import { getSmoothStepPath, Node, Position, useViewport } from "@xyflow/react";
import { memo } from "react";
import { FlowEditorNodeData } from "../utils/layout/nodes-from-steps";
import { NodeData, ZOOM_THRESHOLD_COMBINE } from "./flow-step-node";

const NODE_WIDTH = 224;

function hasNodeData(node: Node): node is Node<NodeData> {
  return (
    node.data !== undefined &&
    typeof node.data === "object" &&
    "step" in node.data &&
    "stepId" in node.data
  );
}

// This shows the connection line when connecting nodes via connection mode
export const ConnectionModeOverlay = memo(function ConnectionModeOverlay({
  connectionMode,
  mousePosition,
  hoveredNodeId,
  nodes,
}: {
  connectionMode: { sourceStepId: string; handleIndex: number } | null;
  mousePosition: { x: number; y: number } | null;
  hoveredNodeId: string | null;
  nodes: Node<FlowEditorNodeData>[];
}) {
  const { x: viewportX, y: viewportY, zoom } = useViewport();

  if (!connectionMode || !mousePosition) {
    return null;
  }

  const sourceNode = nodes.find((n) => n.id === connectionMode.sourceStepId);
  if (!sourceNode || !hasNodeData(sourceNode)) {
    return null;
  }

  const sourceStep = sourceNode.data.step;
  const maxConnections = sourceStep.maxDownstream();
  const shouldCombineHandles =
    zoom < ZOOM_THRESHOLD_COMBINE && maxConnections > 2;

  let sourceHandleX: number;
  const sourceHandleY = sourceNode.position.y + 100;

  if (shouldCombineHandles) {
    sourceHandleX = sourceNode.position.x + NODE_WIDTH / 2;
  } else {
    const spacing = maxConnections > 1 ? 0.8 / (maxConnections - 1) : 0;
    const startOffset = maxConnections > 1 ? 0.1 : 0.5;
    const handlePosition = startOffset + spacing * connectionMode.handleIndex;
    sourceHandleX = sourceNode.position.x + NODE_WIDTH * handlePosition;
  }

  let targetX = mousePosition.x;
  let targetY = mousePosition.y;

  if (hoveredNodeId) {
    const hoveredNode = nodes.find((n) => n.id === hoveredNodeId);
    if (hoveredNode && hoveredNode.id !== "start") {
      targetX = hoveredNode.position.x + NODE_WIDTH / 2;
      targetY = hoveredNode.position.y - 28;
    }
  }

  const [edgePath] = getSmoothStepPath({
    sourceX: sourceHandleX,
    sourceY: sourceHandleY,
    sourcePosition: Position.Bottom,
    targetX,
    targetY,
    targetPosition: Position.Top,
    borderRadius: 20,
  });

  return (
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1000,
      }}
    >
      <g transform={`translate(${viewportX}, ${viewportY}) scale(${zoom})`}>
        <path
          d={edgePath}
          fill="none"
          key="connection-line"
          stroke="black"
          strokeDasharray="5 5"
          strokeLinecap="butt"
          strokeWidth={2 / zoom}
          style={{ animation: "dashdraw 0.5s linear infinite" }}
        />
      </g>
    </svg>
  );
});
